import { useEffect, useMemo, useRef } from "react";
import type { Editor } from "@tiptap/react";
import type { ParseError } from "@/parser/templateParser";
import type { TemplateMeta } from "@/parser/templateSerializer";
import { useMdTiptapSync } from "@/components/unified/useMdTiptapSync";
import { useCodeMirror } from "@/editor/useCodeMirror";
import { buildLineMap } from "@/utils/lineMap";

function makeCmChangeHandler(
  scrollInitiatorRef: React.RefObject<"tiptap" | "cm" | "toc" | null>,
  scrollInitiatorTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>,
  setMdSource: (src: string) => void,
) {
  return (src: string) => {
    scrollInitiatorRef.current = "cm";
    clearTimeout(scrollInitiatorTimerRef.current);
    scrollInitiatorTimerRef.current = setTimeout(() => {
      scrollInitiatorRef.current = null;
    }, 500);
    setMdSource(src);
  };
}

export function useSourceSync(opts: {
  editorRef: React.RefObject<Editor | null>;
  meta: TemplateMeta;
  setMeta: React.Dispatch<React.SetStateAction<TemplateMeta>>;
  mode: "build" | "fill";
  debouncedSave: (source: string) => void;
  setParseErrors: React.Dispatch<React.SetStateAction<ParseError[]>>;
  initialSource: string;
  scrollInitiatorRef: React.RefObject<"tiptap" | "cm" | "toc" | null>;
  scrollInitiatorTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  codeMirrorContainerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { mdSource, setMdSource, syncDocToMd } = useMdTiptapSync({
    editor: opts.editorRef.current,
    initialSource: opts.initialSource,
    meta: opts.meta,
    onMetaChange:
      opts.mode === "build"
        ? (updates) => {
            opts.setMeta((prev) => ({ ...prev, ...(updates as TemplateMeta) }));
          }
        : undefined,
    onParseErrors: opts.setParseErrors,
    onSourceChange:
      opts.mode === "build"
        ? opts.debouncedSave
        : () => {
            /* no-op */
          },
  });

  const lineMapRef = useRef<number[]>([]);
  const lineMap = useMemo(() => buildLineMap(mdSource), [mdSource]);
  useEffect(() => {
    lineMapRef.current = lineMap;
  }, [lineMap]);

  const cmViewRef = useCodeMirror({
    containerRef: opts.codeMirrorContainerRef,
    onChange: makeCmChangeHandler(
      opts.scrollInitiatorRef,
      opts.scrollInitiatorTimerRef,
      setMdSource,
    ),
    value: mdSource,
  });

  return { cmViewRef, lineMapRef, mdSource, setMdSource, syncDocToMd };
}
